import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import {
  fetchScheduleItem, fetchUsers, fetchNotes, fetchPartner,
  fetchPartners, fetchAddresses, assignCourier, createFoodLog,
  updateScheduleItem,
} from "api";
import {
  PageHeader, StatusBadge, Spinner, EmptyState,
  Modal, Table, FormField, Select,
} from "components/common";
import { ChangeStatusModal } from "./ChangeStatusModal";
import { useAuth } from "hooks/useAuth";
import type { ContactUser, EntityAddress, FoodLog, PartnerEntity, ScheduleItemStatus } from "types";

export default function ScheduleItemDetailPage() {
  const { id }          = useParams<{ id: string }>();
  const navigate        = useNavigate();
  const { isAdmin, isCourier } = useAuth();
  const queryClient     = useQueryClient();
  const itemId          = Number(id);
  const [showAssign,        setShowAssign]        = useState(false);
  const [showLogForm,       setShowLogForm]       = useState(false);
  const [showChangeStatus,  setShowChangeStatus]  = useState(false);
  const [editingTime,    setEditingTime]    = useState(false);
  const [editTimeValue,  setEditTimeValue]  = useState("");

  const { mutate: unassignCourier, isPending: unassigning } = useMutation({
    mutationFn: () => updateScheduleItem(itemId, { contact_user_id: null } as never),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ["scheduleItems", itemId] }),
  });

  const { mutate: saveScheduledAt, isPending: savingTime, error: saveTimeError } = useMutation({
    mutationFn: (iso: string) => updateScheduleItem(itemId, { scheduled_at: iso } as never),
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ["scheduleItems", itemId] });
      setEditingTime(false);
    },
  });

  const openTimeEdit = () => {
    // Convert ISO → "YYYY-MM-DDTHH:MM" for datetime-local input
    const d = parseISO(item!.scheduled_at);
    const pad = (n: number) => String(n).padStart(2, "0");
    setEditTimeValue(
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
    setEditingTime(true);
  };

  // "adding" state for deliver_to / received_from
  const [addingDeliverTo,    setAddingDeliverTo]    = useState(false);
  const [deliverToSelect,    setDeliverToSelect]    = useState("");
  const [addingReceivedFrom, setAddingReceivedFrom] = useState(false);
  const [receivedFromSelect, setReceivedFromSelect] = useState("");

  const { data: item, isLoading } = useQuery({
    queryKey: ["scheduleItems", itemId],
    queryFn:  () => fetchScheduleItem(itemId),
  });

  const { data: allUsers = [], isLoading: loadingUsers } = useQuery({
    queryKey: ["users"],
    queryFn:  () => fetchUsers(),
  });

  const { data: partner } = useQuery({
    queryKey: ["partners", item?.partner_entity_id],
    queryFn:  () => fetchPartner(item!.partner_entity_id),
    enabled:  !!item?.partner_entity_id,
  });

  const { data: addresses = [] } = useQuery({
    queryKey: ["addresses", item?.partner_entity_id],
    queryFn:  () => fetchAddresses({ partner_entity_id: String(item!.partner_entity_id) }),
    enabled:  !!item?.partner_entity_id,
  });

  const { data: notes = [] } = useQuery({
    queryKey: ["notes", item?.partner_entity_id],
    queryFn:  () => fetchNotes(item!.partner_entity_id),
    enabled:  !!item?.partner_entity_id,
  });

  const { data: allPartners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const partnerNames = React.useMemo(() => {
    const map: Record<number, string> = {};
    allPartners.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [allPartners]);

  const { mutate: doAssign, isPending: assigning } = useMutation({
    mutationFn: (courierId: number) => assignCourier(itemId, courierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduleItems", itemId] });
      setShowAssign(false);
    },
  });

  const { mutate: saveField, isPending: savingField } = useMutation({
    mutationFn: (params: Record<string, unknown>) => updateScheduleItem(itemId, params as any),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scheduleItems", itemId] }),
  });

  if (isLoading) return <Spinner className="mt-20" />;
  if (!item)     return <EmptyState message="Schedule item not found." />;

  const activeCouriers = allUsers.filter((u) => u.active && u.role === "bridge13_courier");
  const assignedCourier = item.contact_user_id
    ? allUsers.find((u) => u.id === item.contact_user_id)
    : null;
  const courierName = assignedCourier
    ? `${assignedCourier.name_1}${assignedCourier.name_2 ? ` ${assignedCourier.name_2}` : ""}`
    : null;

  const relevantNotes = notes.filter(
    (n) => !n.schedule_item_id || n.schedule_item_id === item.id
  );

  const isPickup     = partner?.type !== "distributor";
  const isDonator    = partner?.type === "donator";
  const isDistributor = partner?.type === "distributor";
  const actionLabel  = isPickup ? "Pickup" : "Drop off";
  const primaryAddress = addresses[0] ?? null;

  const distributorPartners = allPartners.filter((p) => p.type === "distributor" && p.active);
  const donatorPartners     = allPartners.filter((p) => p.type === "donator"      && p.active);

  const currentDeliverTos    = item.deliver_tos    ?? [];
  const currentReceivedFroms = item.received_froms ?? [];
  const currentFoodLogs      = item.food_logs      ?? [];

  /** Statuses a courier is permitted to set */
  const COURIER_STATUSES: ScheduleItemStatus[] = [
    "scheduled", "in_progress", "completed", "abandoned",
  ];

  // Helpers for add/remove of HABTM associations
  const removeDeliverTo = (partnerId: number) => {
    saveField({ deliver_to_ids: currentDeliverTos.filter((p) => p.id !== partnerId).map((p) => p.id) });
  };
  const confirmAddDeliverTo = () => {
    if (!deliverToSelect) return;
    const newId = Number(deliverToSelect);
    if (!currentDeliverTos.find((p) => p.id === newId)) {
      saveField({ deliver_to_ids: [...currentDeliverTos.map((p) => p.id), newId] });
    }
    setDeliverToSelect("");
    setAddingDeliverTo(false);
  };

  const removeReceivedFrom = (partnerId: number) => {
    saveField({ received_from_ids: currentReceivedFroms.filter((p) => p.id !== partnerId).map((p) => p.id) });
  };
  const confirmAddReceivedFrom = () => {
    if (!receivedFromSelect) return;
    const newId = Number(receivedFromSelect);
    if (!currentReceivedFroms.find((p) => p.id === newId)) {
      saveField({ received_from_ids: [...currentReceivedFroms.map((p) => p.id), newId] });
    }
    setReceivedFromSelect("");
    setAddingReceivedFrom(false);
  };

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-2xl space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
      >
        ← Back
      </button>

      <PageHeader
        title={partner ? `${actionLabel} — ${partner.name}` : "Pickup detail"}
        subtitle={"Item #" + item.id}
        action={
          isAdmin ? (
            <div className="flex items-center gap-2">
              {item?.contact_user_id && (
                <button
                  className="btn-secondary"
                  onClick={() => unassignCourier()}
                  disabled={unassigning}
                >
                  {unassigning ? "Removing…" : "Unassign courier"}
                </button>
              )}
              <button className="btn-primary" onClick={() => setShowAssign(true)}>
                Assign courier
              </button>
            </div>
          ) : undefined
        }
      />

      {/* ── Mobile quick-action tiles (hidden on desktop) ──────────────────── */}
      <div className="grid gap-3 md:hidden" style={{ gridTemplateColumns: isDonator ? "1fr 1fr" : "1fr" }}>
        {/* Change status tile */}
        <button
          onClick={() => setShowChangeStatus(true)}
          className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-white border border-gray-200 shadow-sm text-center transition-colors active:bg-gray-50"
        >
          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="text-sm font-medium text-gray-700">Change status</span>
          <StatusBadge status={item.status} />
        </button>

        {/* Add food log tile — donator pickups only */}
        {isDonator && (
          <button
            onClick={() => setShowLogForm(true)}
            className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-brand-50 border border-brand-200 shadow-sm text-center transition-colors active:bg-brand-100"
          >
            <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <span className="text-sm font-medium text-brand-700">Add food log</span>
          </button>
        )}
      </div>

      {/* ── Item details ───────────────────────────────────────────────────── */}
      <div className="card p-6 space-y-4">
        <Row
          label="Scheduled at"
          value={
            editingTime ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="datetime-local"
                  className="input text-sm py-1"
                  value={editTimeValue}
                  onChange={(e) => setEditTimeValue(e.target.value)}
                />
                <button
                  className="btn-primary text-xs py-1 px-3"
                  disabled={!editTimeValue || savingTime}
                  onClick={() => saveScheduledAt(new Date(editTimeValue).toISOString())}
                >
                  {savingTime ? "Saving…" : "Save"}
                </button>
                <button
                  className="btn-secondary text-xs py-1 px-3"
                  disabled={savingTime}
                  onClick={() => setEditingTime(false)}
                >
                  Cancel
                </button>
                {saveTimeError && (
                  <span className="text-xs text-red-500">Failed to save. Try again.</span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span>
                  {parseISO(item.scheduled_at).toLocaleString([], {
                    weekday: "long",
                    year:    "numeric",
                    month:   "long",
                    day:     "numeric",
                    hour:    "numeric",
                    minute:  "2-digit",
                    timeZoneName: "short",
                  })}
                </span>
                {isAdmin && (
                  <button
                    onClick={openTimeEdit}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="Edit scheduled time"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                )}
              </div>
            )
          }
        />
        <Row label="Status" value={<StatusBadge status={item.status} />} />
        <Row
          label="Type"
          value={
            <span className={`badge ${isPickup ? "bg-green-100 text-green-700" : "bg-indigo-100 text-indigo-700"}`}>
              {actionLabel}
            </span>
          }
        />
        <Row
          label="Courier"
          value={
            courierName
              ? <span className="font-medium text-gray-800">{courierName}</span>
              : <span className="text-gray-400">Not assigned</span>
          }
        />

        {/* ── Food logs ─ only for donator partners ───────────────────────  */}
        {isDonator && (
          <Row
            label="Food logs"
            value={
              <div>
                <button
                  className="btn-accent hover:darken text-xs px-2 py-1 font-medium mb-2 hidden md:inline-flex"
                  onClick={() => setShowLogForm(true)}
                >
                  + Add food log
                </button>
                <div className="space-y-1">
                  {currentFoodLogs.length === 0 && (
                    <span className="text-gray-400 text-sm">None</span>
                  )}
                  {currentFoodLogs.map((log) => (
                    <div key={log.id} className="flex items-center gap-3 rounded-lg bg-gray-50 border px-2 py-1">

                      {log.receiving_partner_id && (
                        <span className="text-primary text-sm truncate max-w-[160px]">{partnerNames[log.receiving_partner_id]}</span>
                      )}
                      <span className="text-gray-500 text-sm">{log.poundage} lbs</span>
                      <button
                        className="text-brand-600 hover:underline text-sm flex-1 text-right"
                        onClick={() => navigate("/logs/" + log.id)}
                      >
                        View
                      </button>
                    </div>
                  ))}

                </div>
              </div>
            }
          />
        )}

        {/* ── Deliver to — only for donator partners ─────────────────────── */}
        {isDonator && (
          <Row
            label="Deliver to"
            value={
              <div>
                {/* Add controls — hidden for couriers */}
                {!isCourier && (
                  addingDeliverTo ? (
                    <div className="flex items-center gap-2 mt-1">
                      <Select
                        size="sm"
                        className="w-44"
                        value={deliverToSelect}
                        onChange={(e) => setDeliverToSelect(e.target.value)}
                      >
                        <option value="">Select…</option>
                        {distributorPartners
                          .filter((p) => !currentDeliverTos.find((c) => c.id === p.id))
                          .map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                      </Select>
                      <button
                        className="btn-primary py-1 px-2 text-xs"
                        disabled={!deliverToSelect || savingField}
                        onClick={confirmAddDeliverTo}
                      >
                        Add
                      </button>
                      <button
                        className="text-xs text-gray-400 hover:text-gray-600"
                        onClick={() => { setAddingDeliverTo(false); setDeliverToSelect(""); }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-primary text-white hover:darken text-xs px-2 py-1 font-medium mb-2"
                      onClick={() => setAddingDeliverTo(true)}
                    >
                      + Add
                    </button>
                  )
                )}
                <div className="space-y-1">
                  {currentDeliverTos.length === 0 && (
                    <span className="text-gray-400 text-sm">No receivers added</span>
                  )}
                  {currentDeliverTos.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 rounded-lg bg-gray-50 border px-2 py-1">
                      <span className="text-sm text-gray-800">{p.name}</span>
                      {!isCourier && (
                        <button
                          className="text-gray-400 hover:text-red-500 text-xs leading-none flex-1 text-right"
                          disabled={savingField}
                          onClick={() => removeDeliverTo(p.id)}
                          title="Remove"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            }
          />
        )}

        {/* ── Received from — only for distributor partners ──────────────── */}
        {isDistributor && (
          <Row
            label="Received from"
            value={
              <div>
                {addingReceivedFrom ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Select
                      size="sm"
                      className="w-44"
                      value={receivedFromSelect}
                      onChange={(e) => setReceivedFromSelect(e.target.value)}
                    >
                      <option value="">Select…</option>
                      {donatorPartners
                        .filter((p) => !currentReceivedFroms.find((c) => c.id === p.id))
                        .map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Select>
                    <button
                      className="btn-primary py-1 px-2 text-xs"
                      disabled={!receivedFromSelect || savingField}
                      onClick={confirmAddReceivedFrom}
                    >
                      Add
                    </button>
                    <button
                      className="text-xs text-gray-400 hover:text-gray-600"
                      onClick={() => { setAddingReceivedFrom(false); setReceivedFromSelect(""); }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn-primary text-white hover:darken text-xs px-2 py-1 font-medium mb-2"
                    onClick={() => setAddingReceivedFrom(true)}
                  >
                    + Add
                  </button>
                )}
                <div className="space-y-1">
                  {currentReceivedFroms.length === 0 && (
                    <span className="text-gray-400 text-sm">None</span>
                  )}
                  {currentReceivedFroms.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 rounded-lg bg-gray-50 border px-2 py-1">
                      <span className="text-sm text-gray-800">{p.name}</span>
                      <button
                        className="text-gray-400 hover:text-red-500 text-xs leading-none flex-1 text-right"
                        disabled={savingField}
                        onClick={() => removeReceivedFrom(p.id)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            }
          />
        )}
      </div>

      {/* ── Partner info ───────────────────────────────────────────────────── */}
      {partner && (
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Partner</h2>
          <div className="space-y-3">
            <Row label="Name" value={partner.name} />
            <Row
              label="Type"
              value={<span className="capitalize">{partner.type}</span>}
            />
            {primaryAddress && (
              <>
                <Row
                  label="Address"
                  value={
                    <div>
                      <p>{primaryAddress.address}{primaryAddress.address_2 ? `, ${primaryAddress.address_2}` : ""}</p>
                      <p className="text-gray-500">{primaryAddress.city}, {primaryAddress.state} {primaryAddress.zip}</p>
                    </div>
                  }
                />
                {primaryAddress.coordinates && primaryAddress.coordinates.lat && primaryAddress.coordinates.lng && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-gray-200">
                    <iframe
                      title={`Map of ${primaryAddress.address}`}
                      width="100%"
                      height="200"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${primaryAddress.coordinates.lng - 0.005},${primaryAddress.coordinates.lat - 0.003},${primaryAddress.coordinates.lng + 0.005},${primaryAddress.coordinates.lat + 0.003}&layer=mapnik&marker=${primaryAddress.coordinates.lat},${primaryAddress.coordinates.lng}`}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Notes ──────────────────────────────────────────────────────────── */}
      {relevantNotes.length > 0 && (
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Notes</h2>
          <div className="space-y-2">
            {relevantNotes.map((n) => (
              <div key={n.id} className="flex items-start gap-2 text-sm">
                <span className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${
                  n.schedule_item_id
                    ? "bg-blue-100 text-blue-700"
                    : "bg-amber-100 text-amber-700"
                }`}>
                  {n.schedule_item_id ? "This pickup" : "Partner"}
                </span>
                <span className="text-gray-600">{n.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Assign courier modal ───────────────────────────────────────────── */}
      {showAssign && (
        <Modal title="Assign courier" onClose={() => setShowAssign(false)}>
          {loadingUsers ? (
            <Spinner className="py-8" />
          ) : activeCouriers.length === 0 ? (
            <EmptyState message="No active couriers available." />
          ) : (
            <Table
              data={activeCouriers}
              keyExtractor={(u) => u.id}
              columns={[
                {
                  header:   "Name",
                  accessor: (u: ContactUser) =>
                    u.name_1 + (u.name_2 ? " " + u.name_2 : ""),
                },
                {
                  header:   "",
                  accessor: (u: ContactUser) => (
                    <button
                      className="btn-primary py-1 px-3 text-xs"
                      disabled={assigning}
                      onClick={() => doAssign(u.id)}
                    >
                      Assign
                    </button>
                  ),
                  className: "text-right",
                },
              ]}
            />
          )}
        </Modal>
      )}

      {/* ── Add food log modal ─────────────────────────────────────────────── */}
      {showLogForm && (
        <AddFoodLogModal
          item={item}
          partnerName={partner?.name ?? "Unknown"}
          onClose={() => setShowLogForm(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["scheduleItems", itemId] });
            queryClient.invalidateQueries({ queryKey: ["foodLogs"] });
            setShowLogForm(false);
          }}
        />
      )}

      {/* ── Change status modal ────────────────────────────────────────────── */}
      {showChangeStatus && (
        <ChangeStatusModal
          item={item}
          partnerName={partner?.name ?? "Unknown"}
          allowedStatuses={isCourier ? COURIER_STATUSES : undefined}
          onClose={() => setShowChangeStatus(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["scheduleItems", itemId] });
            setShowChangeStatus(false);
          }}
        />
      )}
    </div>
  );
}

// ─── Add Food Log Modal ───────────────────────────────────────────────────────

function AddFoodLogModal({
  item,
  partnerName,
  onClose,
  onSuccess,
}: {
  item:        { id: number; partner_entity_id: number; schedule_id: number };
  partnerName: string;
  onClose:     () => void;
  onSuccess:   () => void;
}) {
  const [poundage, setPoundage]             = useState("");
  const [description, setDescription]       = useState("");
  const [retailValue, setRetailValue]       = useState("");
  const [receivingPartnerId, setReceivingPartnerId] = useState("");

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const distributors = partners.filter((p: PartnerEntity) => p.type === "distributor" && p.active);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      createFoodLog({
        partner_entity_id:      item.partner_entity_id,
        receiving_partner_id:   receivingPartnerId ? Number(receivingPartnerId) : undefined,
        schedule_item_id:       item.id,
        poundage:               Number(poundage),
        description:            description.trim() || undefined,
        estimated_retail_value: retailValue ? Number(retailValue) : undefined,
        completed_at:           new Date().toISOString(),
      }),
    onSuccess,
  });

  const isValid = poundage && Number(poundage) > 0;

  return (
    <Modal title={`Add food log — ${partnerName}`} onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Donating partner">
          <input
            className="input bg-gray-50 text-gray-500"
            value={partnerName}
            disabled
          />
        </FormField>

        <FormField label="Received by">
          <Select value={receivingPartnerId} onChange={(e) => setReceivingPartnerId(e.target.value)}>
            <option value="">None (optional)</option>
            {distributors.map((p: PartnerEntity) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
          <p className="text-xs text-gray-400 mt-1">
            The downstream partner receiving this food.
          </p>
        </FormField>

        <FormField label="Poundage">
          <input
            className="input"
            type="number"
            min="1"
            value={poundage}
            onChange={(e) => setPoundage(e.target.value)}
            placeholder="e.g. 250"
            autoFocus
          />
        </FormField>

        <FormField label="Description">
          <textarea
            className="input min-h-[60px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What was picked up? (optional)"
          />
        </FormField>

        <FormField label="Estimated retail value ($)">
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={retailValue}
            onChange={(e) => setRetailValue(e.target.value)}
            placeholder="e.g. 500.00 (optional)"
          />
        </FormField>

        <p className="text-xs text-gray-400">
          Schedule item and completion time are pre-filled automatically.
        </p>

        {error && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}

        <div className="flex gap-3 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Save food log"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Row helper ───────────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <span className="text-sm text-gray-500 w-36 shrink-0">{label}</span>
      <span className="text-sm text-gray-900">{value}</span>
    </div>
  );
}
